<script setup>
const props = defineProps({
  type: {
    type: String,
    default: 'info'
  }
});
</script>

<template>
  <div class="alert q-pa-md q-ma-md" :class="`${type}-alert`">
    <slot />
  </div>
</template>

<style lang="scss" scoped>
/*
 * Alert/callout palettes flow through the theme system via the
 * --cc-info-* / --cc-warn-* / --cc-danger-* / --cc-example-* /
 * --cc-story-* CSS variables. Fallbacks preserve the original
 * Cybercore identity colours for users with no theme picked, but
 * are softened from the previous neon-purple example block.
 */
.alert {
  border-radius: 8px;
  border: 2px solid;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(10px);
  line-height: 1.6;
  font-size: var(--fs-small);
  font-family: var(--cc-font-body, 'Orbitron', monospace);

  // Improve text formatting within the alert
  white-space: pre-line;
  text-align: left;

  :deep(p) {
    margin: 0 0 1em 0;
    &:last-child { margin-bottom: 0; }
  }

  :deep(*) {
    line-height: inherit;
  }
}

.info-alert {
  background-color: var(--cc-info-bg, #0a1728);
  color: var(--cc-info-border, #00bcd4);
  border-color: var(--cc-info-border, #00bcd4);
}

.warning-alert {
  background-color: var(--cc-warn-bg, #1a1500);
  color: var(--cc-warn-border, #ff9800);
  border-color: var(--cc-warn-border, #ff9800);
}

.danger-alert,
.error-alert {
  background-color: var(--cc-danger-bg, #1a0a0a);
  color: var(--cc-danger-border, #f44336);
  border-color: var(--cc-danger-border, #f44336);
  padding: 12px;
  font-family: var(--cc-font-body, 'Orbitron', sans-serif);
}

.success-alert {
  background-color: var(--cc-info-bg, #0a1a0a);
  color: var(--cc-info-border, #4caf50);
  border-color: var(--cc-info-border, #4caf50);
}

.story-alert {
  background-color: var(--cc-info-bg, #0a1728);
  color: var(--cc-story-text, #64d9ff);
  border: 2px solid var(--cc-story-border, #00a8cc);
  padding: 12px;
  border-radius: 6px;
  font-family: var(--cc-font-body, 'Orbitron', sans-serif);
  font-style: italic;
  position: relative;
  margin-bottom: 1rem;
  white-space: pre-line;

  a {
    color: var(--cc-story-text, #87efff);
    text-decoration: underline;
    &:hover  { color: var(--cc-accent, #b3f4ff); }
    &:visited { color: var(--cc-story-text, #87efff); }
  }
}

.example-alert {
  background-color: var(--cc-example-bg, #161020);
  color: var(--cc-example-border, #a08acc);
  border: 2px solid var(--cc-example-border, #a08acc);
  padding: 12px;
  border-radius: 4px;
  font-family: var(--cc-font-body, 'Orbitron', sans-serif);
  position: relative;
  margin-bottom: 1rem;
  white-space: pre-line;

  a {
    color: var(--cc-example-border, #a08acc);
    text-decoration: underline;
    &:hover  { color: var(--cc-accent, #c3b0e3); }
    &:visited { color: var(--cc-example-border, #a08acc); }
  }
}
</style>